/**
 * Shared SPA bootstrap. Applies the persisted theme before first paint and
 * mounts the app. Every portal uses this so theme handling cannot diverge.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";
import { animate, inView, scroll } from "motion";
import { readTheme } from "@design/index";
import { initMotion } from "@design/motion.js";
import "@design/tokens.css";

export function boot(node: ReactNode, opts: { motion?: boolean } = {}): void {
  document.documentElement.setAttribute("data-theme", readTheme());
  const el = document.getElementById("root");
  if (!el) throw new Error("#root is missing from the document");
  createRoot(el).render(<StrictMode>{node}</StrictMode>);

  if (opts.motion) {
    // The vendored engine destructures { animate, inView, scroll } off
    // window.Motion. Supplying all three from the bundle rather than a CDN
    // keeps script-src at 'self'; supplying only `animate` made every
    // below-the-fold reveal throw "n is not a function" and silently stop the
    // scan, so most of the page never animated in.
    window.Motion = { animate, inView, scroll } as NonNullable<Window["Motion"]>;
    // After paint, so it measures laid-out elements rather than empty ones.
    requestAnimationFrame(() => requestAnimationFrame(() => initMotion()));
  }
}
